import { useBreakpointValue } from '@chakra-ui/react';
import React from 'react';
import { useContextSelector } from 'use-context-selector';
import wrapColorTheme from '../lib/WrapColorTheme';
import { colors, styled } from '../Stitches';
import EditorContext from './Editor/EditorContext';
import LetterScrollArea from './LetterScrollArea';
import { PaddingBlock, PaddingInline } from './Padding';

const StyledGutterPrimitive = styled('div', {
  boxSizing: 'border-box',
  borderRadius: '$5',
  borderWidth: '$1',
  width: '100%',
  height: 'fit-content',

  transition: 'all 0.2s ease-out',

  variants: {
    theme: {
      light: {
        borderColor: 'rgba(0, 0, 0, 0.32)',
        '&:hover': {
          borderColor: 'rgba(0, 0, 0, 0.64)',
          boxShadow: `0px 0px 2px 0px ${colors.gray600}`,
        },
      },
      dark: {
        '&:hover': {
          borderColor: colors.dark.whiteAlpha300,
          boxShadow: `0px 0px 2px 0px ${colors.dark.whiteAlpha300}`,
        },
      },
    },
  },
});

const StyledGutter = wrapColorTheme(StyledGutterPrimitive);

const FixedHeight = styled('div', {
  variants: {
    fixedHeight: { true: { height: '70vh' }, false: { height: 'fit-content' } },
  },
});

export const SimpleLetterGutter = ({ children }: { children: React.ReactNode }) => {
  const responsivePadding: 'md' | 'xl' | undefined = useBreakpointValue({ base: 'md', md: 'xl' });

  return (
    <StyledGutter>
      <PaddingBlock size={responsivePadding}>
        <PaddingInline size={responsivePadding}>{children}</PaddingInline>
      </PaddingBlock>
    </StyledGutter>
  );
};

export const LetterGutter = ({ children }: { children: React.ReactNode }) => {
  const sideCar = useContextSelector(EditorContext, (ctx) => ctx.sideCar);
  const fixedHeight = !!sideCar.letterId;

  const responsivePadding: 'md' | 'xl' | undefined = useBreakpointValue({ base: 'md', md: 'xl' });
  const paddingBlock = fixedHeight ? 'lg' : responsivePadding;
  const paddingInline = fixedHeight ? 'sm' : responsivePadding;

  return (
    <StyledGutter>
      <PaddingBlock size={paddingBlock}>
        <PaddingInline size={paddingInline}>
          <FixedHeight fixedHeight={fixedHeight}>
            <LetterScrollArea>{children}</LetterScrollArea>
          </FixedHeight>
        </PaddingInline>
      </PaddingBlock>
    </StyledGutter>
  );
};
